<script setup lang="ts">
import type { ProjectLog } from '#trade-activity/queries/logs'
import { useQueryProjectLogs } from '#trade-activity/queries/logs'
import { bindField, revealErrors } from '#core/utils/bind-field'
import { BusinessIntelligenceFlowEnum } from '@forgd/contract/types'
import { formatValue } from '@forgd/formatter'
import { DateInput } from '@forgd/ui-kit/blocks/date-input'
import { Field, FieldContent, FieldError, FieldLabel } from '@forgd/ui-kit/blocks/field'
import { Button } from '@forgd/ui-kit/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@forgd/ui-kit/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@forgd/ui-kit/select'
import { Textarea } from '@forgd/ui-kit/textarea'
import { useMutation } from '@pinia/colada'
import { revalidateLogic, useForm } from '@tanstack/vue-form'
import { format } from 'date-fns'
import { z } from 'zod'

interface TaLogFormProps {
  log?: ProjectLog
}

const props = defineProps<TaLogFormProps>()

const emit = defineEmits(['onSuccess', 'cancel'])

const auth = useAuth()
const toast = useAppToast()
const { captureFlowCompleted } = useFlowTracking()

const schema = z.object({
  createdAt: z.string().min(1, 'Created date is required'),
  categoryId: z.string().uuid('Please select a category'),
  content: z.string().trim().min(1, 'Description is required').max(500, 'Only 500 characters are allowed'),
})

type LogFormValues = z.infer<typeof schema>

const createdAtDate = props.log ? new Date(props.log.createdAt) : new Date()
const createdAt = formatValue(createdAtDate, 'date')
const createdAtInputValue = format(createdAtDate, 'yyyy-MM-dd')
const createdAtId = useId()

const form = useForm({
  defaultValues: {
    createdAt,
    categoryId: props.log?.category.id ?? '',
    content: props.log?.content ?? '',
  } satisfies LogFormValues,
  validationLogic: revalidateLogic({ mode: 'change', modeAfterSubmission: 'change' }),
  validators: { onDynamic: schema },
  onSubmitInvalid: ({ formApi }) => {
    revealErrors(formApi)
    toast.error({
      title: 'Some fields need attention',
      description: 'Please review the highlighted fields.',
    })
  },
  onSubmit: ({ value }) => saveLog(schema.parse(value)),
})

const { data: logCategories } = usePiniaColadaQuery('/projects/{projectId}/business-intelligence/logs/categories', {
  method: 'get',
  params: () => ({
    path: {
      projectId: auth.project!.id,
    },
  }),
})

const {
  mutateAsync: createLogMutation,
  isLoading: createInProgress,
} = useMutation(buildApiMutation('/projects/{projectId}/logs', {
  method: 'post',
}))

const {
  mutateAsync: updateLogMutation,
  isLoading: updateInProgress,
} = useMutation(buildApiMutation('/projects/{projectId}/logs/{logId}', {
  method: 'patch',
}))

const {
  mutateAsync: deleteLogMutation,
  isLoading: deleteInProgress,
} = useMutation(buildApiMutation('/projects/{projectId}/logs/{logId}', {
  method: 'delete',
}))

const { refresh } = useQueryProjectLogs()

const isLoading = computed(() =>
  createInProgress.value ||
  updateInProgress.value ||
  deleteInProgress.value,
)

const categoryOptions = computed(() =>
  (logCategories.value ?? []).map(c => ({ value: c.id, label: c.name })),
)

async function saveLog(data: LogFormValues) {
  const projectId = auth.project?.id
  if (!projectId) {
    return toast.error({
      title: 'Something went wrong, please refresh the page',
      description: 'One of the required access is missing',
    })
  }
  try {
    const isUpdate = !!props.log?.id
    const res = isUpdate
      ? await updateLogMutation({
          path: {
            projectId,
            logId: props.log!.id,
          },
          body: data,
        })
      : await createLogMutation({
          path: {
            projectId,
          },
          body: {
            ...data,
            tool: 'business-intelligence',
          },
        })

    if (res.id) {
      invalidateQueryByUrl('/projects/{projectId}/logs')
      emit('onSuccess')
      await refresh()
      toast.success({
        title: props.log ? 'Log updated successfully' : 'New log saved',
      })
      captureFlowCompleted(isUpdate ? BusinessIntelligenceFlowEnum.UpdateLog : BusinessIntelligenceFlowEnum.CreateLog)
    }
  }
  catch {
    toast.error({
      title: 'Something went wrong',
      description: 'Please try again.',
    })
  }
}

async function deleteLog() {
  try {
    if (!auth.project?.id || !props.log?.id) {
      return toast.error({
        title: 'Something went wrong, please refresh the page',
        description: 'One of the required access is missing',
      })
    }

    await deleteLogMutation({
      path: {
        projectId: auth.project.id,
        logId: props.log.id,
      },
    })
    invalidateQueryByUrl('/projects/{projectId}/logs')
    emit('onSuccess')
    await refresh()
    toast.success({
      title: 'Log deleted successfully',
    })
    captureFlowCompleted(BusinessIntelligenceFlowEnum.DeleteLog)
  }
  catch {
    toast.error({
      title: 'Something went wrong',
      description: 'Please try again.',
    })
  }
}
</script>

<template>
  <form novalidate @submit.prevent="form.handleSubmit()">
    <Card>
      <CardHeader>
        <CardTitle>{{ props.log ? 'Edit Log Details' : 'Add Log Details' }}</CardTitle>
      </CardHeader>
      <CardContent class="flex flex-col gap-4">
        <div class="grid w-full grid-cols-2 gap-5">
          <Field>
            <FieldLabel :for="createdAtId">
              {{ log ? 'Date Created & Edited' : 'Date Created' }}
            </FieldLabel>
            <FieldContent>
              <DateInput :id="createdAtId" :model-value="createdAtInputValue" disabled />
            </FieldContent>
          </Field>
          <form.Field v-slot="{ field }" name="categoryId">
            <Field>
              <FieldLabel v-bind="bindField(field).label">
                Log Category
              </FieldLabel>
              <FieldContent>
                <Select
                  :model-value="bindField(field).control.modelValue"
                  @update:model-value="bindField(field).control['onUpdate:modelValue']"
                >
                  <SelectTrigger
                    :id="bindField(field).control.id"
                    :error="bindField(field).control.error"
                    :aria-describedby="bindField(field).control['aria-describedby']"
                    @blur="field.handleBlur()"
                  >
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem v-for="opt in categoryOptions" :key="opt.value" :value="opt.value">
                      {{ opt.label }}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FieldError v-bind="bindField(field).error" />
              </FieldContent>
            </Field>
          </form.Field>
        </div>
        <form.Field v-slot="{ field }" name="content">
          <Field>
            <FieldLabel v-bind="bindField(field).label">
              Log Description (500 characters Max.)
            </FieldLabel>
            <FieldContent>
              <Textarea
                v-bind="bindField(field).control"
                :name="field.name"
                placeholder="Enter here the details of the log."
                :enable-resize="false"
                :rows="5"
                maxlength="500"
              />
              <FieldError v-bind="bindField(field).error" />
            </FieldContent>
          </Field>
        </form.Field>
      </CardContent>
      <CardFooter>
        <div v-if="props.log" class="grid w-full grid-cols-3 gap-2">
          <Button
            type="button"
            variant="outline"
            :disabled="isLoading"
            @click="emit('cancel')"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            :loading="isLoading"
            :disabled="isLoading"
            @click="deleteLog"
          >
            Delete
          </Button>
          <Button
            type="submit"
            :loading="isLoading"
            :disabled="isLoading"
          >
            Save
          </Button>
        </div>
        <div v-else class="grid w-full grid-cols-2 gap-2">
          <Button
            type="button"
            variant="outline"
            :disabled="isLoading"
            @click="emit('cancel')"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            :loading="isLoading"
            :disabled="isLoading"
          >
            Save
          </Button>
        </div>
      </CardFooter>
    </Card>
  </form>
</template>
